import bbox from '@turf/bbox'
import bboxPolygon from '@turf/bbox-polygon'
import * as olProj from 'ol/proj'

import { batchPromises, delay } from '../async'

import Exports from './exports'
import Subscriptions from './subscriptions'
import Farm from './farm'
import Field from './field'
import Comments from './comments'
import Auth from './auth'
import { isString } from 'lodash'
import { toSnakeCase } from '../object-utils'
import PlantDetection from './plant-detection'
import Job from './job'
import YieldEstimation from './yield-estimation'
import Admin from './admin'
import User from './user'
import Project from './project'
import { splitOnFileExtension } from '../string-utils'
import pako from 'pako'
import { toQueryString } from '../utils/url-utils'
import { splitExt } from '../utils/path-utils'
import S3Multipart from '../utils/s3-multipart'

const retryStatuses = [408, 502, 503]

export function isNetworkErrorStatus(httpStatus) {
  return retryStatuses.indexOf(httpStatus) >= 0
}

export function apiFetch(url, options = {}) {
  let retryCount = 0
  return tryFetch()

  function tryFetch() {
    return window.fetch(url, options).then(
      function (res) {
        if (res.status === 200 || res.status === 201) {
          return res.json()
        } else if (res.status === 202 && options.retryHttp202) {
          return delay(tryFetch, 1000)
        } else if (res.status > 200 && res.status < 300) {
          retryCount = 0
          return {}
        } else if (isNetworkErrorStatus(res.status)) {
          return retry() || fail(res)
        } else if (
          res.status === 422 &&
          (options.handleHttp422 || options.handleHttp422 === undefined)
        ) {
          return handleTimeoutSession()
        } else {
          return fail(res)
        }
      },
      function (err) {
        if (err instanceof TypeError) {
          const result = retry()
          if (result) return result
        }
        throw err
      }
    )
  }

  function retry() {
    // Network error, retry
    if (++retryCount < (options.retries || 3)) {
      return delay(tryFetch, retryCount * retryCount * 1000)
    }
  }

  function fail(res) {
    const method = (options && options.method) || 'GET'
    const errorMessage = `${method} ${url} failed: ${res.status} ${res.statusText}`
    return res
      .json()
      .catch(function () {
        throw new Error(errorMessage)
      })
      .then(function (jsonError) {
        const error = new ApiFetchError(errorMessage, res, jsonError)
        Object.assign(error, jsonError)
        throw error
      })
  }

  function handleTimeoutSession() {
    if (
      confirm(
        'Sorry, it looks like you have been logged out.\n\nClick OK to reload the page and sign in again.'
      )
    ) {
      window.location.reload()
    } else {
      throw new Error(
        'Invalid CSRF token (HTTP 422), most likely session timed out).'
      )
    }
  }
}

export default function ApiClient(routes, authenticityToken, accessToken) {
  this.authenticityToken = authenticityToken
  this.accessToken = accessToken
  this.routes = routes
  this.exports = new Exports(this)
  this.subscriptions = new Subscriptions(this)
  this.comments = new Comments(this)
  this.auth = new Auth(this)
  this.farm = new Farm(this)
  this.field = new Field(this)
  this.plantDetection = new PlantDetection(this)
  this.job = new Job(this)
  this.yieldEstimation = new YieldEstimation(this)
  this.admin = new Admin(this)
  this.user = new User(this)
  this.project = new Project(this)
}

ApiClient.prototype = {
  fetch: function (routeName, params, options, urlOptions) {
    const hasBody = options && options.body
    const hasFormBody = hasBody && options.body instanceof FormData
    const hasStringBody = hasBody && isString(options.body)

    if (hasBody && !hasFormBody && !hasStringBody) {
      options.body = JSON.stringify(toSnakeCase(options.body))
    }

    options = {
      headers: {
        'X-CSRF-Token': this.authenticityToken,
        Authorization: this.accessToken
          ? `Bearer ${this.accessToken}`
          : undefined,
      },
      ...options,
    }

    if (
      options.body &&
      isString(options.body) &&
      options.body.length > 16 * 1024
    ) {
      options.headers['Content-Encoding'] = 'gzip'
      options.body = pako.gzip(options.body)
    }

    if (!hasFormBody && !options.headers['Content-Type']) {
      options.headers['Content-Type'] = 'application/json'
    }

    return apiFetch(this.createUrl(routeName, params, urlOptions), options)
  },

  getProjectCost: function (numberCaptures, plantCountArea, crop) {
    return this.fetch('upload_project_cost_index', null, null, {
      queryParams: {
        number_captures: numberCaptures,
        plant_counts_area: plantCountArea,
        crop,
      },
    })
  },

  createProject: function (
    farmId,
    fieldName,
    projectProperties,
    metadataProperties,
    imagesGeoJson
  ) {
    const extent = bbox(imagesGeoJson)
    const epsg3857Extent = olProj.transformExtent(
      extent,
      'EPSG:4326',
      'EPSG:3857'
    )
    // If the extent for some reason ends up being a point
    // (min === max), make it just a teeny-tiny bit larger
    // to avoid various errors related to empty extents
    if (epsg3857Extent[0] === epsg3857Extent[2]) {
      epsg3857Extent[2] += 1e-2
    }
    if (epsg3857Extent[1] === epsg3857Extent[3]) {
      epsg3857Extent[3] += 1e-2
    }
    const envelope = bboxPolygon(epsg3857Extent)

    return this.fetch(
      'projects',
      {},
      {
        method: 'POST',
        body: JSON.stringify({
          project: {
            ...projectProperties,
            farm_id: farmId,
            fields: fieldName,
            original_extent: JSON.stringify({
              type: 'MultiPolygon',
              coordinates: [envelope.geometry.coordinates],
            }),
            metadata: JSON.stringify(metadataProperties),
          },
        }),
      }
    )
  },

  getProject: function (projectId) {
    return this.fetch('project', { id: projectId }).then(project => {
      // For performance reasons, metadata is transferred as a string
      // rather than an object, so we manually deserialize it here
      // to look like the original metadata object.
      project.metadata = { data: JSON.parse(project.metadata) }
      return project
    })
  },

  updateProject: function (projectId, update) {
    return this.fetch(
      'project',
      { id: projectId },
      { method: 'PUT', body: JSON.stringify({ project: update }) }
    )
  },

  getSourceImageryGeoJSON: function (projectId) {
    return this.fetch('project_photos', { project_id: projectId })
  },

  getProjectPhotoUrl: function (projectId, filename, downloadFilename) {
    var parts = splitOnFileExtension(filename)
    return (
      this.routes.project_photo
        .replace(':project_id', projectId)
        .replace(':id', parts[0])
        .replace('(.:format)', '.' + parts[1]) +
      '?' +
      toQueryString({
        access_token: this.accessToken,
        download_filename: downloadFilename,
      })
    )
  },

  getProjectPhotosUploadUrl: function (projectId) {
    return this.fetch('upload_url_project_photos', { project_id: projectId })
  },

  uploadProjectPhotos: function (projectId, files, options) {
    return this.getProjectPhotosUploadUrl(projectId).then(postInfo =>
      uploadFiles(postInfo, files, options)
    )
  },

  startProcessing: function (projectId, options) {
    return this.fetch(
      'start_processing_project',
      { id: projectId },
      { method: 'POST', body: JSON.stringify(options || {}) }
    )
  },

  submitProjectForReview: function (projectId) {
    return this.fetch(
      'submit_for_review_project',
      { id: projectId },
      { method: 'POST' }
    )
  },

  getProjectResultUrl: function (projectId, filename, downloadFilename) {
    var parts = filename.split('.')
    return this.fetch(
      'project_result',
      {
        id: projectId,
        filename: parts[0],
      },
      null,
      {
        format: '.' + parts[1],
        queryParams: downloadFilename
          ? {
              download_filename: downloadFilename,
            }
          : null,
      }
    ).then(({ url }) => url)
  },

  getCustomUploads: function (projectId) {
    return this.fetch('project_custom_uploads', { project_id: projectId })
  },

  saveSettings: function (projectId, settings) {
    return this.fetch(
      'api_v1_project_settings',
      { project_id: projectId },
      { method: 'POST', body: JSON.stringify({ data: { settings: settings } }) }
    )
  },

  getCustomLayers: function (projectId) {
    return this.fetch('project_custom_layers', { project_id: projectId })
  },

  createCustomLayer: function (projectId, name, data) {
    return this.fetch(
      'project_custom_layers',
      { project_id: projectId },
      {
        method: 'POST',
        body: JSON.stringify({
          layer: {
            name: name,
            data: data,
          },
        }),
      }
    )
  },

  updateCustomLayer: function (projectId, layerId, data) {
    return this.fetch(
      'project_custom_layer',
      { project_id: projectId, id: layerId },
      {
        method: 'PUT',
        body: JSON.stringify({
          layer: {
            data: data,
          },
        }),
      }
    )
  },

  getCustomLayerImageUrl: function (projectId, layerId, filename) {
    var parts = filename.split('.')
    return (
      this.routes.project_custom_layer_image
        .replace(':project_id', projectId)
        .replace(':custom_layer_id', layerId)
        .replace(':id', parts[0])
        .replace('(.:format)', '.' + parts[1]) +
      (this.accessToken ? '?access_token=' + this.accessToken : '')
    )
  },

  getCustomLayerUploadUrl: function (projectId, layerId) {
    return this.fetch('upload_url_project_custom_layer', {
      project_id: projectId,
      id: layerId,
    })
  },

  uploadCustomLayerFiles: function (projectId, layerId, files, options) {
    return this.getCustomLayerUploadUrl(projectId, layerId).then(postInfo =>
      uploadFiles(postInfo, files, options)
    )
  },

  deleteCustomLayer: function (projectId, layerId) {
    return this.fetch(
      'project_custom_layer',
      {
        project_id: projectId,
        id: layerId,
      },
      {
        method: 'DELETE',
      },
      { format: '' }
    )
  },

  fetchProjectAnnotationsIndividualImages(projectId, ref) {
    return this.getCustomLayers(projectId).then(layers =>
      layers.filter(
        layer => layer.data.type === 'annotations' && layer.data.ref === ref
      )
    )
  },

  /**
   *
   * @param {number} projectId
   * @param {string=} ref
   * @param {object=} imageFeature
   * @returns {Promise<{data: {geojson:{features: import('@turf/helpers').Feature[]}}>}}
   */
  async fetchAnnotations(projectId, ref, imageFeature) {
    const filterImage = layer =>
      layer.data.type === 'annotations' &&
      layer.data.ref === ref &&
      layer.data.imageRef === imageFeature.properties.filename

    const filterOrtho = layer =>
      layer.data.type === 'annotations' && !layer.data.imageRef

    const filter = imageFeature ? filterImage : filterOrtho
    const layers = await this.getCustomLayers(projectId)

    return layers.filter(filter)[0]
  },

  async saveAnnotations(projectId, ref, imageFeature, layerId, annotations) {
    if (ref) {
      annotations.ref = ref
    }
    if (imageFeature) {
      annotations.imageRef = imageFeature.properties.filename
    }

    if (layerId) {
      await this.updateCustomLayer(projectId, layerId, annotations)
      return layerId
    } else {
      const { id } = await this.createCustomLayer(
        projectId,
        'Annotations',
        annotations
      )
      console.log('Save annotations result', id)
      return id
    }
  },

  getCustomUploadPostInfo: function (projectId) {
    return this.fetch('upload_url_project_custom_upload', {
      project_id: projectId,
    })
  },

  getCustomUploadUrl: function (projectId, filename, downloadFilename) {
    var parts = splitOnFileExtension(filename)
    return this.createUrl(
      'project_custom_upload',
      {
        project_id: projectId,
        id: parts[0],
      },
      {
        format: '.' + parts[1],
        queryParams: downloadFilename
          ? {
              download_filename: downloadFilename,
            }
          : null,
      }
    )
  },

  createCustomUpload: function (projectId, filename) {
    return this.fetch(
      'create_custom_upload_admin_project',
      { id: projectId },
      {
        method: 'POST',
        body: JSON.stringify({
          filename: filename,
        }),
      }
    )
  },

  getCustomUploadPartUrl: function (projectId, filename, uploadId, partNumber) {
    return this.fetch(
      'custom_upload_part_url_admin_project',
      { id: projectId },
      {
        method: 'POST',
        body: JSON.stringify({
          filename: filename,
          upload_id: uploadId,
          part_number: partNumber,
        }),
      }
    )
  },

  completeCustomUpload: function (projectId, filename, uploadId, etags) {
    return this.fetch(
      'complete_custom_upload_admin_project',
      { id: projectId },
      {
        method: 'POST',
        body: JSON.stringify({
          filename: filename,
          upload_id: uploadId,
          etags: etags,
        }),
      }
    )
  },

  /**
   *
   * @param {number} projectId
   * @param {File} file
   * @param {(uploadedBytes: number, totalBytes: number) => void} onProgress
   * @param {string|undefined} uploadName
   * @returns {Promise<string>}
   */
  uploadOrtho: function (projectId, file, onProgress, uploadName = null) {
    const [root] = splitExt(file.name)
    // Make sure the extension is always .tiff, since that is what the
    // pipeline supports; other extensions will not be correctly processed
    const filename = uploadName || `${root}.tiff`
    const s3Upload = new S3Multipart({
      createUpload: f => this.createOrthoUpload(projectId, filename),
      getPartUrl: (f, uploadId, partNumber, partSize) =>
        this.getOrthoUploadPartUrl(
          projectId,
          filename,
          uploadId,
          partNumber,
          partSize
        ),
      completeUpload: (f, uploadId, etags) =>
        this.completeOrthoUpload(projectId, filename, uploadId, etags),
      onProgress,
      partSize: 20 * 1024 * 1024,
      retries: 7,
      retryBackoffTimeMs: retries => retries * 15000,
    })

    return s3Upload.upload(file).then(({ url }) => url)
  },

  createOrthoUpload: function (projectId, filename) {
    return this.fetch(
      'create_ortho_upload_project',
      { id: projectId },
      {
        method: 'POST',
        body: JSON.stringify({
          filename: filename,
        }),
      }
    )
  },

  getOrthoUploadPartUrl: function (projectId, filename, uploadId, partNumber) {
    return this.fetch(
      'ortho_upload_part_url_project',
      { id: projectId },
      {
        method: 'POST',
        body: JSON.stringify({
          filename: filename,
          upload_id: uploadId,
          part_number: partNumber,
        }),
      }
    )
  },

  completeOrthoUpload: function (projectId, filename, uploadId, etags) {
    return this.fetch(
      'complete_ortho_upload_project',
      { id: projectId },
      {
        method: 'POST',
        body: JSON.stringify({
          filename: filename,
          upload_id: uploadId,
          etags: etags,
        }),
      }
    )
  },

  createPlantCountsUpload: function (projectId) {
    return this.fetch(
      'create_plant_counts_upload_admin_project',
      { id: projectId },
      {
        method: 'POST',
      }
    )
  },

  getPlantCountsUploadPartUrl: function (projectId, uploadId, partNumber) {
    return this.fetch(
      'plant_counts_upload_part_url_admin_project',
      { id: projectId },
      {
        method: 'POST',
        body: JSON.stringify({
          upload_id: uploadId,
          part_number: partNumber,
        }),
      }
    )
  },

  completePlantCountsUpload: function (projectId, uploadId, etags) {
    return this.fetch(
      'complete_plant_counts_upload_admin_project',
      { id: projectId },
      {
        method: 'POST',
        body: JSON.stringify({
          upload_id: uploadId,
          etags: etags,
        }),
      }
    )
  },

  deleteCustomUpload: function (projectId, filename) {
    return this.fetch(
      'project_custom_upload',
      {
        project_id: projectId,
        id: filename,
      },
      {
        method: 'DELETE',
      },
      { format: '' }
    )
  },

  defaultSearch: { sort: 'id', dir: 'asc' },

  searchProjects: function (search) {
    return this.fetch(
      'admin_projects_search',
      {},
      {},
      { queryParams: { ...this.defaultSearch, ...search } }
    )
  },

  searchUsers: function (search) {
    return this.fetch(
      'admin_users_search',
      {},
      {},
      { queryParams: { ...this.defaultSearch, ...search } }
    )
  },

  searchFarms: function (search) {
    return this.fetch(
      'admin_farms_search',
      {},
      {},
      { queryParams: { ...this.defaultSearch, ...search } }
    )
  },

  searchFields: function (search) {
    return this.fetch(
      'admin_fields_search',
      {},
      {},
      { queryParams: { ...this.defaultSearch, ...search } }
    )
  },

  searchUserProducts: function (search) {
    return this.fetch(
      'admin_purchases_search',
      {},
      {},
      { queryParams: { ...this.defaultSearch, ...search } }
    )
  },

  createField: function (name) {
    return this.fetch(
      'admin_fields',
      {},
      {
        method: 'POST',
        body: JSON.stringify({ name: name }),
      }
    ).then(function (response) {
      return response.field_id
    })
  },

  deleteField: function (fieldId) {
    return this.fetch(
      'admin_field',
      { id: fieldId },
      {
        method: 'DELETE',
      }
    )
  },

  cloneProject: function (projectId, userId, fieldId, fieldName, farmId) {
    return this.fetch(
      'clone_admin_project',
      { id: projectId },
      {
        method: 'POST',
        body: JSON.stringify({
          dst_user_id: userId,
          dst_field_id: fieldId,
          dst_field_name: fieldName,
          dst_farm_id: farmId,
        }),
      }
    )
  },

  getUser: function (userId) {
    return this.fetch('admin_user', { id: userId })
  },

  addUserPermission: function (userId, permissionId) {
    return this.fetch(
      'admin_user_permissions',
      { user_id: userId },
      {
        method: 'POST',
        body: JSON.stringify({
          id: permissionId,
        }),
      }
    )
  },

  getFarm: function (farmId) {
    return this.fetch('admin_farm', { id: farmId })
  },

  createFarm: function (farm) {
    return this.fetch(
      'admin_farms',
      {},
      {
        method: 'POST',
        body: JSON.stringify({ farm: farm }),
      }
    )
  },

  saveFarm: function (farmId, farm) {
    return this.fetch(
      'admin_farm',
      { id: farmId },
      {
        method: 'PUT',
        body: JSON.stringify({ farm: farm }),
      }
    )
  },

  addUserRole: function (userId, roleId) {
    return this.fetch(
      'admin_user_roles',
      { user_id: userId },
      {
        method: 'POST',
        body: JSON.stringify({
          id: roleId,
        }),
      }
    )
  },

  deleteFarm: function (farmId) {
    return this.fetch(
      'admin_farm',
      { id: farmId },
      {
        method: 'DELETE',
      }
    )
  },

  getFlightPlans: function () {
    return this.fetch('flight_plans')
  },

  createFlightPlan: function (name, mode, plan) {
    return this.fetch(
      'flight_plans',
      {},
      {
        method: 'POST',
        body: JSON.stringify({
          flight_plan: {
            name: name,
            mode: mode,
            plan: JSON.stringify(plan.toGeoJson()),
          },
        }),
      }
    )
  },

  saveFlightPlan: function (id, name, mode, plan) {
    return this.fetch(
      'flight_plan',
      { id: id },
      {
        method: 'PUT',
        body: JSON.stringify({
          flight_plan: {
            name: name,
            mode: mode,
            plan: JSON.stringify(plan.toGeoJson()),
          },
        }),
      }
    )
  },

  deleteFlightPlan: function (id) {
    return this.fetch(
      'flight_plan',
      { id: id },
      {
        method: 'DELETE',
      }
    )
  },

  createFlightEvent: function (flightPlanId, body) {
    return this.fetch('flight_events', null, {
      method: 'POST',
      body: JSON.stringify({
        flight_event: {
          flight_plan_id: flightPlanId,
          body: JSON.stringify(body),
        },
      }),
    })
  },

  moveProject: function (projectId, userId, fieldId, fieldName, farmId) {
    return this.fetch(
      'admin_project',
      { id: projectId },
      {
        method: 'PUT',
        body: JSON.stringify({
          project: {
            user_id: userId,
            field_id: fieldId,
            field_name: fieldName,
            farm_id: farmId,
          },
        }),
      }
    )
  },

  removeUserPermission: function (userId, permissionId) {
    return this.fetch(
      'admin_user_permission',
      { user_id: userId, id: permissionId },
      {
        method: 'DELETE',
      }
    )
  },

  removeUserRole: function (userId, roleId) {
    return this.fetch(
      'admin_user_role',
      { user_id: userId, id: roleId },
      {
        method: 'DELETE',
      }
    )
  },

  getPermissions: function () {
    return this.fetch('admin_permissions')
  },

  getRoles: function () {
    return this.fetch('admin_roles')
  },

  getCurrentPermissions: function () {
    return this.fetch('permissions_api_v1_users', {}).then(function (ff) {
      return ff.reduce(function (a, f) {
        a[f] = true
        return a
      }, {})
    })
  },

  addUserFarm: function (userId, farmId) {
    return this.fetch(
      'admin_user_farms',
      { user_id: userId },
      {
        method: 'POST',
        body: JSON.stringify({
          id: farmId,
        }),
      }
    )
  },

  addUserField: function (userId, fieldId) {
    return this.fetch(
      'admin_user_fields',
      { user_id: userId },
      {
        method: 'POST',
        body: JSON.stringify({
          id: fieldId,
        }),
      }
    )
  },

  getSourceImageryUrl: function (projectId) {
    return this.createUrl(
      'source_imagery_project',
      { id: projectId },
      { format: '.zip' }
    )
  },

  getCustomLayersImageryUrl: function (projectId) {
    return this.createUrl(
      'custom_layers_imagery_project',
      { id: projectId },
      { format: '.zip' }
    )
  },

  removeUserFarm: function (userId, farmId) {
    return this.fetch(
      'admin_user_farm',
      { user_id: userId, id: farmId },
      {
        method: 'DELETE',
      }
    )
  },

  removeUserField: function (userId, fieldId) {
    return this.fetch(
      'admin_user_field',
      { user_id: userId, id: fieldId },
      {
        method: 'DELETE',
      }
    )
  },

  detectPlots: function (projectIdentifier, areaGeoJson, tolerance, bands) {
    return this.job
      .fetchJobResult(projectIdentifier, 'detect_plots', {
        plotArea: areaGeoJson,
        simplificationTolerance: tolerance,
        bands,
        postprocess: true,
      })
      .then(({ urls }) => apiFetch(urls[0]))
  },

  getProducts: function () {
    return this.fetch('products')
  },

  createUrl: function (routeName, params, options) {
    options = options || {}
    const { queryParams, format } = options
    const routeUrl = this.routes[routeName]

    if (!routeUrl) {
      throw new Error(`No route named ${routeName}.`)
    }

    if (!routeUrl) {
      throw new Error(`Undefined route with name "${routeName}".`)
    }

    return (
      Object.keys(params || {})
        .reduce(function (url, key) {
          return url.replace(':' + key, params[key])
        }, routeUrl)
        .replace('(.:format)', format != null ? format : '.json') +
      (queryParams
        ? '?' +
          Object.keys(queryParams)
            .map(function (k) {
              return `${k}=${encodeURIComponent(queryParams[k])}`
            })
            .join('&')
        : '')
    )
  },
}

function uploadFiles(postInfo, files, options) {
  const {
    progressCallback,
    preproccessCallback,
    numberParallelUploads,
    retries,
    backOffTime,
  } = options || {}

  return batchPromises(
    files,
    preproccessCallback
      ? f => preproccessCallback(f).then(uploadFile)
      : uploadFile,
    numberParallelUploads || postInfo.number_parallel_hint || 3
  )

  function uploadFile(f) {
    let attempts = 0
    return tryUpload()

    function tryUpload() {
      let fetchPromise
      if (postInfo.type === 'url_template') {
        const url = postInfo.template.replace('$filename', f.name)
        fetchPromise = fetch(url, {
          method: 'POST',
          body: f,
          headers: { 'Content-Type': f.type },
        })
      } else if (postInfo.type === 'post_form_data') {
        const form = new FormData()
        for (const key in postInfo.fields) {
          form.append(key, postInfo.fields[key])
        }
        form.append('key', postInfo.key_prefix + f.name)
        form.append('Content-Type', f.type)
        form.append('file', f)

        fetchPromise = fetch(postInfo.url, {
          method: 'POST',
          body: form,
        })
      }

      attempts++
      return fetchPromise
        .then(response => {
          if (!response.ok) {
            return response.text().then(
              text => {
                throw new Error(
                  `Unexpected HTTP status ${response.status} ${response.statusText} when uploading ${f.name}. Error:\n\n${text}`
                )
              },
              err => {
                throw new Error(
                  `Unexpected HTTP status ${response.status} ${response.statusText} when uploading ${f.name}. Additionally, reading response body caused "${err}".`
                )
              }
            )
          }
        })
        .then(() => progressCallback && progressCallback(f, 'done'))
        .catch(err => {
          if (attempts <= (retries || 2)) {
            return new Promise((resolve, reject) => {
              setTimeout(() => {
                tryUpload().then(resolve).catch(reject)
              }, (backOffTime || 10000) * attempts)
            })
          } else {
            throw new FileUploadError(
              `File upload failed for ${f.name} (${f.size} bytes)`,
              err
            )
          }
        })
    }
  }
}

export class ApiFetchError extends Error {
  constructor(message, response) {
    super(message)
    this.response = response
  }
}

class FileUploadError extends Error {
  constructor(message, cause = undefined) {
    super(cause ? `${message}: ${cause}` : message)
    this.cause = cause
  }
}
